import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import BookService from "../../services/BookService";
import BookCard from "../BookComponents/BookCard";
import Spinner from "../CommonComponents/Spinner";
import { useSearchParams } from "react-router-dom";
import Pagination from "../CommonComponents/Pagination";
import { SearchInput } from "../CommonComponents/Components";
import { getAllGenres } from "../../services/GenreService";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 16px;
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
`;

const StyledSelect = styled.select`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
  width: auto;
  min-width: 160px;
`;

function BookSearch({ user }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState(searchParams.get("q") || "");
    const [field, setField] = useState(searchParams.get("f") || "name");
    const [genreId, setGenreId] = useState(searchParams.get("genre") ? Number(searchParams.get("genre")) : null);
    const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "date");
    const [sortDir, setSortDir] = useState(searchParams.get("sortDir") || "desc");
    const [genres, setGenres] = useState([]);
    const [books, setBooks] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const inputRef = useRef(null);
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        getAllGenres().then(setGenres).catch(() => {});
    }, []);

    useEffect(() => {
        const hasText = query.length >= 3;
        const hasGenre = !!genreId;

        if (!hasText && !hasGenre) {
            setBooks([]);
            setTotalPages(0);
            setLoading(false);
            return;
        }
        setLoading(true);

        const handler = setTimeout(async () => {
            try {
                const res = await BookService.searchBooks(query, field, currentPage, 10, genreId, sortBy, sortDir);
                setBooks(res.content);
                setTotalPages(Math.ceil(res.totalElements / 10));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [query, field, genreId, sortBy, sortDir, currentPage]);

    useEffect(() => {
        const params = {};
        if (query) params.q = query;
        if (field) params.f = field;
        if (genreId) params.genre = genreId;
        if (sortBy) params.sortBy = sortBy;
        if (sortDir) params.sortDir = sortDir;
        if (currentPage) params.page = currentPage;
        setSearchParams(params, { replace: true });
    }, [query, field, genreId, sortBy, sortDir, currentPage, setSearchParams]);

    return (
        <Container>
            <ControlsRow>
                <SearchInput
                    ref={inputRef}
                    type="text"
                    placeholder="Pretraži knjige..."
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setCurrentPage(0);
                    }}
                />
                <StyledSelect value={field} onChange={(e) => { setField(e.target.value); setCurrentPage(0); }}>
                    <option value="name">Ime knjige</option>
                    <option value="author">Autor</option>
                    <option value="keyword">Ključne riječi</option>
                </StyledSelect>
                <StyledSelect
                    value={genreId ?? ""}
                    onChange={(e) => {
                        setGenreId(e.target.value ? Number(e.target.value) : null);
                        setCurrentPage(0);
                    }}
                >
                    <option value="">Svi žanrovi</option>
                    {genres.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                </StyledSelect>
                <StyledSelect
                    value={`${sortBy}_${sortDir}`}
                    onChange={(e) => {
                        const [by, dir] = e.target.value.split("_");
                        setSortBy(by);
                        setSortDir(dir);
                        setCurrentPage(0);
                    }}
                >
                    <option value="date_desc">Datum dodavanja (najnovije)</option>
                    <option value="date_asc">Datum dodavanja (najstarije)</option>
                    <option value="name_asc">Ime (A–Z)</option>
                    <option value="name_desc">Ime (Z–A)</option>
                </StyledSelect>
            </ControlsRow>

            {loading ? <Spinner /> : books.map((book) => <BookCard key={book.id} book={book} user={user} />)}

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </Container>
    );
}
export default BookSearch;
